import { ApiService } from "../services/api_service";

/**
 * Main payment success handler
 * Calls the /booking/confirm API which handles:
 * - Ticket availability updates
 * - Booking creation
 * - User profile updates
 * - Confirmation email
 *
 * @param {string} paymentId - Payment ID from Razorpay
 * @param {object} checkoutData - All checkout related data
 * @param {object} user - Current user object
 * @param {function} setShowPaymentModal - Function to hide payment modal
 * @param {function} onSuccess - Success callback (receives bookingId)
 * @param {function} onError - Error callback (receives error message)
 */
export const handlePaymentSuccess = async (
  paymentId,
  checkoutData,
  user,
  setShowPaymentModal,
  onSuccess,
  onError
) => {
  try {
    console.log("🎉 Payment successful! Starting post-payment processing...");
    console.log("💳 Payment ID:", paymentId);

    // Validate inputs
    if (!paymentId) {
      throw new Error("Payment ID is missing");
    }

    if (!checkoutData) {
      throw new Error("Checkout data is missing");
    }

    if (!user || !user.uid) {
      throw new Error("User data is missing");
    }

    const {
      eventId,
      selectedSlot,
      selectedTickets,
      selectedDate,
      totalPrice,
      grandTotal,
      ticketDetails,
      eventName,
      eventLocation,
      eventData,
      razorpayOrderId,
      appliedCoupon
    } = checkoutData;

    // Validate required fields
    const requiredFields = {
      eventId,
      selectedSlot,
      selectedTickets,
      selectedDate,
      ticketDetails,
      eventName
    };

    for (const [key, value] of Object.entries(requiredFields)) {
      if (!value) {
        throw new Error(`Required field missing: ${key}`);
      }
    }

    console.log("✅ All required data validated");

    // Get the primary ticket type
    const primaryTicketType = selectedSlot.tickets.find(ticket => selectedTickets[ticket.id]);
    if (!primaryTicketType) {
      throw new Error("No matching ticket found in selected slot");
    }

    // Calculate total ticket count
    const totalTicketsCount = Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);

    // Convert dates to ISO strings
    const eventDateString = selectedDate.dateOfEvent?.toDate
      ? selectedDate.dateOfEvent.toDate().toISOString()
      : new Date(selectedDate.dateOfEvent).toISOString();

    const ticketSlotString = selectedSlot.startTime?.toDate
      ? selectedSlot.startTime.toDate().toISOString()
      : new Date(selectedSlot.startTime).toISOString();

    // Build the confirm API payload
    const confirmPayload = {
      paymentId: paymentId,
      eventId: eventId,
      dateId: selectedDate.id,
      slotId: selectedSlot.id,
      ticketId: primaryTicketType.id,
      ticketCount: totalTicketsCount,
      eventDate: eventDateString,
      ticketSlot: ticketSlotString,
      userId: user.uid,
      userName: ticketDetails.name,
      userPhone: ticketDetails.phone,
      userEmail: ticketDetails.email,
      grandTotalPrice: parseFloat(grandTotal?.toFixed(2) || totalPrice),
      eventName: eventName,
      eventVenue: eventLocation || "",
      organiserId: eventData?.organiserId || eventData?.userId || "",
      bookingType: "web"
    };

    console.log("📄 Confirm booking payload:", confirmPayload);

    // Call the unified confirm API
    const response = await ApiService.confirmBooking(confirmPayload);

    console.log("📦 Confirm booking response:", response);

    // Extract booking ID from response
    const bookingId = response?.data?.id || response?.id;

    if (!bookingId) {
      console.warn("⚠️ API response didn't contain booking ID", response);
    }

    console.log("✅ Booking confirmed via API with ID:", bookingId);

    // Redeem coupon if one was applied (non-blocking — never block booking on failure).
    // Send the same `tickets` list used in /validate, and use the Razorpay order_id as
    // the idempotency key (redeeming twice with the same order_id is a safe no-op).
    if (appliedCoupon?.couponCode) {
      try {
        const redeemTickets = Object.entries(selectedTickets).map(([id, qty]) => {
          const t = selectedSlot.tickets.find(tk => tk.id === id);
          return { ticket_type_id: id, quantity: qty, price_per_ticket: t?.price ?? 0 };
        });
        await ApiService.redeemCoupon({
          coupon_code: appliedCoupon.couponCode,
          user_id: user.uid,
          event_id: eventId,
          order_id: razorpayOrderId || bookingId,
          tickets: redeemTickets,
        });
      } catch (e) {
        console.error('Coupon redeem failed (non-blocking):', e);
      }
    }

    // Update UI state
    setShowPaymentModal(false);

    // Call success callback
    if (onSuccess) {
      onSuccess(bookingId);
    }

    console.log("🎊 Payment processing completed successfully!");

    return {
      success: true,
      bookingId: bookingId,
      message: "Booking confirmed successfully!"
    };

  } catch (error) {
    console.error("💥 Payment success handler error:", error);

    // Hide payment modal on error
    setShowPaymentModal(false);

    // Call error callback
    if (onError) {
      onError(error.message);
    }

    return {
      success: false,
      error: error.message,
      message: "Failed to complete booking. Please contact support."
    };
  }
};